import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";

import { caregiversApi } from "@/api/directory";
import { visitsApi } from "@/api/scheduling";
import { Alert } from "@/components/Alert";
import { EmptyState } from "@/components/EmptyState";
import { FormSelect } from "@/components/FormSelect";
import { LoadingBlock } from "@/components/LoadingBlock";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { VisitModal } from "./VisitModal";

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function addDays(d, days) {
  const date = new Date(d);
  date.setDate(date.getDate() + days);
  return date;
}

function formatDateIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function visitChipClass(status) {
  return cn(
    "absolute left-1 right-1 z-10 overflow-hidden rounded-md border-0 border-l-[3px] px-2 py-1.5 text-left text-xs shadow-sm transition-transform hover:scale-[1.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring",
    status === "cancelled" &&
      "border-l-muted-foreground bg-muted text-muted-foreground line-through",
    status === "completed" && "border-l-emerald-600 bg-emerald-500/10 text-emerald-800",
    status === "in_progress" && "border-l-amber-500 bg-amber-500/10 text-amber-900",
    !["cancelled", "completed", "in_progress"].includes(status) &&
      "border-l-primary bg-primary/10 text-primary",
  );
}

const HOURS = Array.from({ length: 17 }, (_, i) => 6 + i);

export function WeeklySchedulePage() {
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()));
  const [caregivers, setCaregivers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState("");
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [initialSlotDate, setInitialSlotDate] = useState(null);
  const [initialSlotTime, setInitialSlotTime] = useState(null);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentMonday, i));
  const weekStartStr = formatDateIso(weekDays[0]);
  const weekEndStr = formatDateIso(weekDays[6]);

  const loadSchedule = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        start_date: `${weekStartStr}T00:00:00`,
        end_date: `${weekEndStr}T23:59:59`,
        page_size: 200,
      };
      if (selectedCaregiver) {
        params.caregiver = selectedCaregiver;
      }
      const [vRes, cgRes] = await Promise.all([
        visitsApi.list(params),
        caregiversApi.list({ page_size: 100 }),
      ]);
      setVisits(vRes.results || []);
      setCaregivers(cgRes.results || []);
    } catch (err) {
      setError(err.message || "Failed to load schedule.");
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    loadSchedule();
  }, [weekStartStr, selectedCaregiver]);

  const handlePrevWeek = () => setCurrentMonday((prev) => addDays(prev, -7));
  const handleNextWeek = () => setCurrentMonday((prev) => addDays(prev, 7));
  const handleToday = () => setCurrentMonday(getMonday(new Date()));

  const handleSlotClick = (dateStr, hour) => {
    setSelectedVisit(null);
    setInitialSlotDate(dateStr);
    setInitialSlotTime(`${String(hour).padStart(2, "0")}:00`);
    setModalOpen(true);
  };

  const handleVisitClick = (v, e) => {
    e.stopPropagation();
    setSelectedVisit(v);
    setModalOpen(true);
  };

  const weekLabel = (
    <>
      {weekDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} –{" "}
      {weekDays[6].toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}
    </>
  );

  const caregiverOptions = [
    { value: "", label: "All Caregivers" },
    ...caregivers.map((cg) => ({
      value: cg.id,
      label: `${cg.first_name} ${cg.last_name}`,
    })),
  ];

  if (loading && !hasLoaded) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Weekly Schedule"
        description="Plan and assign client visits for the selected week."
        actions={
          <Button
            type="button"
            onClick={() => {
              setSelectedVisit(null);
              setInitialSlotDate(formatDateIso(new Date()));
              setInitialSlotTime("09:00");
              setModalOpen(true);
            }}
          >
            + Schedule Visit
          </Button>
        }
      />

      <PageBody>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={handlePrevWeek}>
              ← Prev
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleNextWeek}>
              Next →
            </Button>
            <span className="px-1 text-sm font-semibold">{weekLabel}</span>
          </div>

          <div className="min-w-[180px]">
            <FormSelect
              id="schedule-caregiver-filter"
              value={selectedCaregiver}
              onValueChange={setSelectedCaregiver}
              options={caregiverOptions}
              placeholder="All Caregivers"
              triggerClassName="min-w-[180px]"
            />
          </div>
        </div>

        <Alert>{error}</Alert>

        {loading && <LoadingBlock label="Loading weekly schedule" />}

        {!loading && !error && visits.length === 0 && (
          <EmptyState
            icon={CalendarDays}
            tone="cyan"
            title="No visits this week"
            message="Choose an open time slot to schedule the first visit."
          />
        )}

        {!loading && (
          <div className="max-w-full overflow-x-auto rounded-lg">
            <div className="grid min-w-[760px] grid-cols-[60px_repeat(7,minmax(0,1fr))] overflow-hidden rounded-lg border border-border bg-card">
              <div className="border-b border-r border-border bg-muted/40 p-2 text-center text-sm font-semibold">
                Time
              </div>

              {weekDays.map((day) => {
                const isToday = formatDateIso(day) === formatDateIso(new Date());
                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      "border-b border-r border-border p-2 text-center text-sm font-semibold last:border-r-0",
                      isToday ? "bg-primary/10" : "bg-muted/40",
                    )}
                  >
                    <div>{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div
                      className={cn(
                        "text-base",
                        isToday ? "text-primary" : "text-foreground",
                      )}
                    >
                      {day.getDate()}
                    </div>
                  </div>
                );
              })}

              <div className="border-r border-border bg-muted/30">
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="h-[52px] border-b border-dashed border-border pt-1 text-center text-[0.72rem] text-muted-foreground"
                  >
                    {String(hour).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {weekDays.map((day) => {
                const dateStr = formatDateIso(day);
                const dayVisits = visits.filter((v) => v.start_time.startsWith(dateStr));

                return (
                  <div
                    key={dateStr}
                    className="relative border-r border-border last:border-r-0"
                  >
                    {HOURS.map((hour) => (
                      <button
                        type="button"
                        key={hour}
                        className="block h-[52px] w-full appearance-none border-0 border-b border-dashed border-border bg-transparent p-0 hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                        onClick={() => handleSlotClick(dateStr, hour)}
                        title={`Schedule at ${dateStr} ${hour}:00`}
                        aria-label={`Schedule a visit on ${day.toLocaleDateString()} at ${String(hour).padStart(2, "0")}:00`}
                      />
                    ))}

                    {dayVisits.map((v) => {
                      const startDate = new Date(v.start_time);
                      const endDate = new Date(v.end_time);

                      const startHour =
                        startDate.getHours() + startDate.getMinutes() / 60;
                      const endHour = endDate.getHours() + endDate.getMinutes() / 60;

                      const topPx = Math.max(0, (startHour - 6) * 52);
                      const heightPx = Math.max(36, (endHour - startHour) * 52);

                      return (
                        <button
                          type="button"
                          key={v.id}
                          className={visitChipClass(v.status)}
                          style={{ top: `${topPx}px`, height: `${heightPx}px` }}
                          onClick={(e) => handleVisitClick(v, e)}
                          aria-label={`Edit ${v.client_name}'s visit from ${startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} to ${endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                        >
                          <div className="text-[0.78rem] font-semibold">{v.client_name}</div>
                          <div>
                            {startDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            -{" "}
                            {endDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          {v.caregiver_name && (
                            <div className="text-[0.7rem] opacity-85">
                              👤 {v.caregiver_name}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PageBody>

      {modalOpen && (
        <VisitModal
          visit={selectedVisit}
          initialDate={initialSlotDate}
          initialTime={initialSlotTime}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            loadSchedule();
          }}
        />
      )}
    </PageShell>
  );
}
